import { BasicLayout } from '@components/layouts/basic-layout'
import { BaseBlock } from '@components/base-block'
import { BaseButton } from '@components/base-button'
import { BaseCell } from '@components/base-cell'
import { BaseGrid } from '@components/base-grid'
import { BaseInputWithLabel } from '@components/base-input/input-with-label'
import {
  AlertModal,
  ConfirmModal,
  ModalStateContainer,
} from '@components/base-modal'
import { BaseTextarea } from '@components/base-textarea'
import {
  HeadingM,
  HeadingS,
  LabelM,
  LabelS,
  LabelXS,
  ParagraphS,
} from '@components/base-typography'
import { InfoBoxWithIcon } from '@components/info-box-with-icon'
import { formOptions } from '@lib/forms/formOptions'
import { useFileUploaderWithForm } from '@modules/files/hooks/use-file-uploader-with-form'
import { fileSize } from '@lib/utils/file-size'
import { getInputProps, getTextareaProps } from '@lib/utils/get-input-props'
import { useMe } from '@modules/users/hooks/use-me'
import { colors } from '@styles/colors'
import { mediaQuery } from '@styles/media-query'
import { FileUploader } from 'baseui/file-uploader'
import { NextPage } from 'next'
import { borderStyle, margin, padding } from 'polished'
import { useEffect } from 'react'
import {
  Controller,
  FormProvider,
  NestedValue,
  useForm,
  useFormContext,
} from 'react-hook-form'
import { useStyletron } from 'styletron-react'
import { useState } from 'react'
import { useCreateInquiry } from '@modules/inquiries/hooks/use-create-inquiry'
import { useRouter } from 'next/router'
import { generatePath } from '@lib/utils/generate-path'
import { Routes } from '@lib/routes'

interface InquiryWritePageProps {}

export interface InquiryForm {
  email: string
  title: string
  question: string
  files?: File[]
  warning?: string
}

const InquiryWritePage: NextPage<InquiryWritePageProps> = ({}) => {
  const methods = useForm<InquiryForm>({
    defaultValues: {
      email: '',
      files: [],
    },
  })

  return (
    <BaseGrid spaceTop={[24, 48, 72]}>
      <BaseCell span={[4, 6, 8]} skip={[0, 1, 0]}>
        <HeadingM>1대1 문의</HeadingM>
        <HeadingS color={colors.gray500} marginTop="24px">
          궁금한 내용이 있다면 언제든 연락해주세요.
        </HeadingS>
        <HeadingS color={colors.gray500}>
          최대 3영업일 이내에 답변 드리겠습니다.
        </HeadingS>
        <FormProvider {...methods}>
          <UploadForm />
        </FormProvider>
      </BaseCell>
      <BaseCell span={[4, 3, 3]} skip={[0, 1, 1]} spaceTop={[80, 80, 0]}>
        <InfoBoxWithIcon icon="Info">
          <LabelM weight={700}>운영시간</LabelM>
          <ParagraphS
            whiteSpace="pre-wrap"
            marginTop="8px"
          >{`평일 10:00~19:00\n주말 및 공휴일 휴무`}</ParagraphS>
          <ParagraphS
            marginTop="16px"
            color={colors.gray500}
            whiteSpace="pre-line"
          >
            고객님의 문의에 대해서는 확인 후 답변 드리고 있으나 접수된 순서에
            따라 답변이 진행되어 안내가 지연될 수 있는 점 참고 및 양해
            부탁드립니다.
          </ParagraphS>
        </InfoBoxWithIcon>
      </BaseCell>
    </BaseGrid>
  )
}

InquiryWritePage.getLayout = (page) => (
  <BasicLayout helmetTitle="Ontol | 1대1 문의">{page}</BasicLayout>
)

export default InquiryWritePage

const UploadForm = () => {
  const [css] = useStyletron()
  const { me, loading } = useMe()
  const router = useRouter()
  const [inquiryId, setInquiryId] = useState<string | null>(null)

  const [confirmModal, setConfirmModal] = useState(false)

  const { onDrop, onDropRejected, onRemoveFile } = useFileUploaderWithForm()
  const { onSubmit, submitError, submitLoading, onSubmitError } =
    useCreateInquiry()

  const {
    register,
    formState: { errors },
    control,
    watch,
    handleSubmit,
    resetField,
    setValue,
    clearErrors,
  } = useFormContext<InquiryForm>()
  const files = watch('files')

  useEffect(() => {
    if (!loading && me && me.email) {
      setValue('email', me.email)
    }
  }, [loading, me, setValue])

  useEffect(() => {
    if (errors?.warning?.message) {
      setTimeout(() => {
        clearErrors('warning')
      }, 3000)
    }
  }, [clearErrors, errors?.warning?.message])

  return (
    <>
      <form
        className={css({
          marginTop: '32px',
        })}
        onSubmit={handleSubmit(() => {
          setConfirmModal(true)
        })}
      >
        <BaseInputWithLabel
          label="이메일"
          placeholder="example@gmail.com"
          {...getInputProps(register('email', formOptions.EMAIL))}
          error={errors.email?.message}
          spaceTop={16}
        />
        <BaseInputWithLabel
          label="제목"
          placeholder="제목을 입력해주세요"
          {...getInputProps(register('title', formOptions.TITLE))}
          error={errors.title?.message}
          spaceTop={16}
        />
        <BaseBlock flexbox column position="relative">
          <BaseTextarea
            spaceTop={16}
            placeholder="내용을 입력해주세요"
            {...getTextareaProps(
              register('question', {
                required: '내용을 입력해주세요',
                maxLength: {
                  value: 2000,
                  message: '내용은 2000자 이하로 입력해주세요',
                },
              })
            )}
            maxLength={2000}
            error={errors?.question?.message}
          />
          <LabelS color={colors.gray500} alignSelf="flex-end" marginTop="8px">
            {`${watch('question')?.length || 0}/2000`}
          </LabelS>
        </BaseBlock>

        <BaseBlock
          flexbox
          marginTop="24px"
          alignItems="center"
          justifyContent="space-between"
          columnGap="24px"
        >
          <LabelXS color={colors.gray500}>
            20MB 이하의 파일을 총 5개까지 첨부하실 수 있습니다
          </LabelXS>
          <Controller
            name="files"
            control={control}
            render={() => (
              <FileUploader
                accept="image/*, application/pdf, application/zip, text/csv, application/vnd.ms-excel"
                maxSize={1024 * 1024 * 20}
                onDrop={onDrop}
                onDropRejected={onDropRejected}
                overrides={{
                  FileDragAndDrop: {
                    style: {
                      ...borderStyle('none'),
                      ...padding('0px'),
                      backgroundColor: 'transparent',
                    },
                  },
                  ContentMessage: {
                    component: () => null,
                  },
                  ButtonComponent: {
                    component: (props: any) => (
                      <BaseButton
                        $type="tertiary"
                        $size="s"
                        leading="Attach"
                        type="button"
                        {...props}
                        overrides={{
                          BaseButton: {
                            style: {
                              ...margin('0px'),
                              whiteSpace: 'pre',
                            },
                          },
                        }}
                      >
                        파일 첨부
                      </BaseButton>
                    ),
                  },
                }}
              />
            )}
          />
        </BaseBlock>
        <BaseBlock
          flexbox
          columnGap="16px"
          rowGap="8px"
          flexWrap
          marginTop="16px"
        >
          {files &&
            files.length > 0 &&
            files.map((file: File) => (
              <div key={file.name}>
                <BaseButton
                  key={file.name}
                  $size="s"
                  $type="secondary"
                  trailing="Close"
                  onClick={() => onRemoveFile(file.name)}
                >
                  {file.name} ({fileSize(file.size)})
                </BaseButton>
              </div>
            ))}
        </BaseBlock>
        {errors?.warning?.message && (
          <LabelS marginTop="16px" color={colors.green300}>
            {errors.warning.message}
          </LabelS>
        )}
        {errors?.files?.message && (
          <LabelS marginTop="16px" color={colors.red400}>
            {errors.files.message}
          </LabelS>
        )}
        <BaseBlock flexbox flexCenter marginTop="48px">
          <BaseButton
            overrides={{
              BaseButton: {
                style: {
                  width: '100%',
                  [mediaQuery.TABLET]: {
                    width: '240px',
                  },
                },
              },
            }}
            type="submit"
          >
            문의하기
          </BaseButton>
        </BaseBlock>
      </form>
      <ModalStateContainer>
        {({ isConfirmationOpen, toggleConfirm }) => {
          return (
            <>
              <ConfirmModal
                onClose={() => {
                  setConfirmModal(false)
                }}
                onConfirm={onSubmit((id) => {
                  toggleConfirm(true)
                  if (id) {
                    setInquiryId(id)
                  }
                })}
                isOpen={confirmModal}
                title="작성한 내용을 제출하시겠습니까?"
                description=""
                isLoading={submitLoading}
              />
              <AlertModal
                onClose={() => {
                  toggleConfirm(false)
                  setConfirmModal(false)
                  resetField('title')
                  resetField('question')
                  resetField('files')
                  if (inquiryId) {
                    router.push(
                      generatePath(Routes.SETTINGS_INQUIRIES_DETAIL, {
                        id: inquiryId,
                      })
                    )
                  }
                  if (!me) {
                    resetField('email')
                  }
                }}
                isOpen={isConfirmationOpen}
                title="문의한 내용이 제출되었습니다."
                description={`최대 3영업일 이내에 답변 드리겠습니다.\n답변 내용은 이메일을 통해 확인하실 수 있습니다`}
                type="success"
              />
            </>
          )
        }}
      </ModalStateContainer>
      <AlertModal
        onClose={() => onSubmitError(null)}
        isOpen={Boolean(submitError)}
        title="오류가 발생했습니다."
        description="1대1 문의 신청에 실패하였습니다."
      />
    </>
  )
}
